"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";

const headOnlySelectors = [
  'meta[name="description"]',
  'meta[name="keywords"]',
  'meta[property^="og:"]',
  'meta[name^="twitter:"]',
  'link[rel="canonical"]',
];

const getUniqueSelector = (element: Element): string | null => {
  if (element.tagName === "META") {
    const name = element.getAttribute("name");
    const property = element.getAttribute("property");

    if (name) return `meta[name="${CSS.escape(name)}"]`;
    if (property) return `meta[property="${CSS.escape(property)}"]`;
  }

  if (
    element.tagName === "LINK" &&
    element.getAttribute("rel")?.toLowerCase() === "canonical"
  ) {
    return 'link[rel="canonical"]';
  }

  return null;
};

const moveHeadOnlyTagsFromBody = () => {
  headOnlySelectors.forEach((selector) => {
    document.body.querySelectorAll(selector).forEach((element) => {
      const uniqueSelector = getUniqueSelector(element);
      const existingHeadElement = uniqueSelector
        ? document.head.querySelector(uniqueSelector)
        : null;

      if (existingHeadElement && existingHeadElement !== element) {
        existingHeadElement.remove();
      }

      document.head.appendChild(element);
    });
  });
};

const ClientHeadMetaGuard = () => {
  const pathname = usePathname();

  useEffect(() => {
    let isMoving = false;

    const cleanup = () => {
      if (isMoving) return;

      isMoving = true;
      moveHeadOnlyTagsFromBody();
      isMoving = false;
    };

    cleanup();

    const observer = new MutationObserver(cleanup);
    observer.observe(document.body, { childList: true, subtree: true });

    const timeoutId = window.setTimeout(cleanup, 0);

    return () => {
      observer.disconnect();
      window.clearTimeout(timeoutId);
    };
  }, [pathname]);

  return null;
};

export default ClientHeadMetaGuard;
